
import { useState, useRef, useEffect } from "react";
import { useWebSocketChat } from "@/hooks/chat/useWebSocketChat";
import { ConversationHeader } from "./ConversationHeader";
import { MessageArea } from "./MessageArea";
import { ChatInputArea } from "./ChatInputArea";
import { ChatSidebar } from "./ChatSidebar";
import { QuickActions } from "./QuickActions";
import { useIsMobile } from "@/hooks/use-mobile";

export function EnhancedChatInterface() {
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const {
    messages,
    isLoading,
    isConnected,
    sendMessage,
    selectedAgent,
    changeAgent,
    selectedAgentId
  } = useWebSocketChat();

  // Debug logging for selectedAgent
  useEffect(() => {
    console.log('🎯 EnhancedChatInterface selectedAgent changed:', {
      selectedAgentId,
      selectedAgent: selectedAgent ? { id: selectedAgent.id, name: selectedAgent.name } : null
    });
  }, [selectedAgent, selectedAgentId]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  const handleSendMessage = (content: string) => {
    sendMessage(content);
  };

  const handleEmailSend = (emailData: any) => {
    // Convert email data to a formatted message
    const emailText = `📧 **Email Draft Created**\n\n**To:** ${emailData.data.to}\n**Subject:** ${emailData.data.subject}\n\n**Content:**\n${emailData.data.body}`;
    sendMessage(emailText);
  };

  const toggleSidebar = () => {
    setSidebarOpen(prev => !prev);
  };

  return (
    <div className="h-screen flex bg-gradient-to-br from-background via-background to-muted/20">
      {/* Sidebar */}
      <ChatSidebar 
        open={sidebarOpen}
        onToggle={toggleSidebar}
        selectedAgent={selectedAgent}
        selectedAgentId={selectedAgentId}
        onAgentChange={changeAgent}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        isMobile={isMobile}
      />

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0 relative">
        {/* Header */}
        <ConversationHeader
          agent={selectedAgent}
          isConnected={isConnected}
          onToggleSidebar={toggleSidebar}
          sidebarOpen={sidebarOpen}
        />

        {/* Messages Area */}
        <MessageArea
          messages={messages}
          isLoading={isLoading}
          isConnected={isConnected}
          searchQuery={searchQuery}
          messagesEndRef={messagesEndRef}
        />

        {/* Quick Actions */}
        {messages.length === 0 && isConnected && (
          <QuickActions onSendMessage={handleSendMessage} />
        )}

        {/* Input Area */}
        <ChatInputArea 
          onSendMessage={handleSendMessage}
          isLoading={isLoading}
          isConnected={isConnected}
          onToggleSidebar={toggleSidebar}
          onEmailSend={handleEmailSend}
        />
      </div>
    </div>
  );
}
